import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  SaveButton,
  CancelButton,
  EditButton,
  DeleteButton
} from './ui/ActionButtons';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Search, Filter, Calendar, TrendingUp, TrendingDown, DollarSign, AlertCircle, Trash2, Pencil, Plus, Calculator, ChevronDown, Check, CheckCircle2, Clock } from 'lucide-react';
import { useAppData } from '../hooks/useAppData';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import * as FileSaver from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { TableExportMenu } from './ui/TableExportMenu';
import { db } from '../firebaseConfig';
import { updateDoc, doc, setDoc } from 'firebase/firestore';
import { TableFilterBar } from './ui/TableFilterBar';
import { TablePeriodSelector } from './ui/TablePeriodSelector';
import { DownloadReceiptButton } from './ui/DownloadReceiptButton';
import { DetalleAbono } from '../utils/receiptTemplates';
import { matchesMonthYear, matchesYear } from '../utils/dateFilters';

export function PaymentSystem() {
  // --- Declaraciones de fecha globales para todo el componente ---
  const now = new Date();
  const mesActual = now.getMonth();
  const anioActual = now.getFullYear();
  const { lots, sellers, markedPayments, addLot, updateLot, addMovimiento, updateMovimiento, deleteMovimiento, expenses, config } = useAppData();
  const currentYear = new Date().getFullYear();
  const [selectedMonth, setSelectedMonth] = useState('Todos');
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVendedor, setSelectedVendedor] = useState('Todos los vendedores');
  const [selectedEstado, setSelectedEstado] = useState('Todos los estados');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCobro, setSelectedCobro] = useState<any>(null);
  const [abonoForm, setAbonoForm] = useState({
    numeroDocumento: '', // No. Doc. del banco (obligatorio)
    transferencia: '', // No. Autorización (opcional)
    boleta: '', // Mantener para compatibilidad con datos históricos (oculto en UI)
    fecha: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [shouldSearch, setShouldSearch] = useState(false); // Controla si debe buscar

  // Estados para abono manual
  const [modalAbonoManualOpen, setModalAbonoManualOpen] = useState(false);
  const [selectedLotManual, setSelectedLotManual] = useState<any>(null); // Mantener para compatibilidad con código existente
  const [selectedLotesManual, setSelectedLotesManual] = useState<Array<{ lot: any; monto: string }>>([]); // Array de lotes con montos
  const [abonoManualForm, setAbonoManualForm] = useState({
    monto: '',
    fecha: new Date().toISOString().split('T')[0],
    numeroDocumento: '', // No. Doc. del banco (obligatorio)
    transferencia: '', // No. Autorización (opcional)
    boleta: '', // Mantener para compatibilidad con datos históricos (oculto en UI)
    observaciones: ''
  });
  const [previewCalculoManual, setPreviewCalculoManual] = useState<any>(null);
  const [comboboxOpen, setComboboxOpen] = useState(false);

  const months = [
    'Todos', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  // Obtener vendedores únicos de los lotes
  const vendedores: string[] = ['Todos los vendedores', ...sellers.map((v: any) => v.name)];

  const estados = [
    'Todos los estados',
    'Pendiente',
    'Pendiente Parcial',
    'Al día',
    'Vencido'
  ];

  // Generar cuotas agrupadas por grupo de venta - SOLO cuando hay criterio de búsqueda
  const generateCuotasAgrupadas = () => {
    // NO generar cuotas si no hay búsqueda activa
    if (!shouldSearch) {
      return [];
    }

    const search = searchTerm.trim().toLowerCase();
    const cuotas: any[] = [];

    // Agrupar lotes por grupo de venta
    const grupos: Record<string, any[]> = {};
    lots.filter((lot: any) => lot.status === 'crédito' && lot.paymentType === 'pagos').forEach((lot: any) => {
      const key = `${lot.seller}|${lot.buyerName}|${lot.buyerDPI}|${lot.saleDate}|${lot.receiptNumber}`;
      if (!grupos[key]) grupos[key] = [];
      grupos[key].push(lot);
    });

    // OPTIMIZACIÓN: Filtrar grupos por búsqueda ANTES de generar cuotas
    const gruposFiltrados = Object.values(grupos).filter((grupo) => {
      if (grupo.length === 0) return false;

      const comprador = grupo[0].buyerName?.toLowerCase() || '';

      // Búsqueda por lote: puede ser ID exacto o parte de un ID compuesto
      const lotesIds = grupo.map((l: any) => l.id?.toString().toLowerCase().trim());

      // Buscar en IDs:
      // 1. Coincidencia exacta: "53" === "53" ✓
      // 2. Parte de ID compuesto: "180" en "180-181" ✓
      // Usar separadores para evitar falsos positivos: "1" no debe encontrar "215"
      const matchLote = lotesIds.some((loteId: string) => {
        // Coincidencia exacta
        if (loteId === search) return true;

        // Buscar como palabra completa en IDs compuestos (separados por -, /, ,)
        const partes = loteId.split(/[-\/,\s]+/);
        return partes.some((parte: string) => parte === search);
      });

      const matchComprador = comprador.includes(search);

      return matchLote || matchComprador;
    });

    // Generar cuotas SOLO de los grupos que coinciden con la búsqueda
    gruposFiltrados.forEach((grupo) => {
      // Datos comunes del grupo
      const comprador = grupo[0].buyerName;
      const vendedor = grupo[0].seller;
      const vendedorObj = sellers.find((s: any) => s.id === vendedor || s.account === vendedor || s.name === vendedor);
      const vendedorNombre = vendedorObj ? vendedorObj.name : (vendedor || '-');
      const recibo = grupo[0].receiptNumber;
      const fechaVenta = grupo[0].saleDate;
      const lotes = grupo.map(l => l.id).join(', ');
      // Sumar valores del grupo
      const totalValue = grupo.reduce((sum, l) => sum + (l.totalValue || 0), 0);
      const payments = grupo.flatMap(l => l.payments || []);
      const numberOfPayments = grupo[0].numberOfPayments || grupo[0].numeroPagos || 1;
      const pagoEnganche = payments.find((p: any) => p.type === 'enganche');
      const enganche = pagoEnganche ? Number(pagoEnganche.amount) : 0;
      const cuotaMensual = (totalValue - enganche) / numberOfPayments;
      const fechaVentaDate = new Date(fechaVenta || new Date());
      for (let i = 1; i <= numberOfPayments; i++) {
        const fechaVencimiento = new Date(fechaVentaDate);
        fechaVencimiento.setMonth(fechaVencimiento.getMonth() + i);
        const fechaVencimientoYMD = fechaVencimiento.toISOString().split('T')[0];
        // Buscar todos los pagos para esta cuota usando la función robusta
        const lotesDelGrupo = grupo.map((lot: any) => lot.id?.toString() || lot.id);
        const abonosSincronizadosGrupo = grupo.flatMap((lot: any) => getAbonosSincronizadosRobusto(lot, expenses));
        const pagosCuota = abonosSincronizadosGrupo.filter((p: any) =>
          p.type === 'abono' &&
          (p.cuotaNumero === i.toString() || p.cuotaNumero === i)
        );

        // Calcular monto pagado para esta cuota
        const montoPagadoCuota = pagosCuota.reduce((sum, p) => sum + Number(p.amount), 0);

        let estado = 'Pendiente';
        let montoCuotaMostrar = cuotaMensual;
        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0); // Normalizar a inicio del día para comparación justa
        const fechaVencimientoNorm = new Date(fechaVencimiento);
        fechaVencimientoNorm.setHours(0, 0, 0, 0);

        if (montoPagadoCuota >= cuotaMensual) {
          estado = 'Al día';
        } else if (montoPagadoCuota > 0) {
          // Si hay pago parcial, mostrar el saldo pendiente
          montoCuotaMostrar = cuotaMensual - montoPagadoCuota;
          // Verificar si está vencida aunque tenga pago parcial
          if (fechaVencimientoNorm < hoy) {
            estado = 'Vencido';
          } else {
            estado = 'Pendiente Parcial';
          }
        } else if (fechaVencimientoNorm < hoy) {
          estado = 'Vencido';
        }


        cuotas.push({
          comprador,
          vendedor,
          vendedorNombre,
          lotes,
          recibo,
          fechaVenta,
          cuota: i.toString(),
          cuotaNumero: i.toString(),
          fechaVencimiento: fechaVencimientoYMD,
          cuotaFechaVencimiento: fechaVencimientoYMD,
          montoCuota: montoCuotaMostrar.toFixed(2),
          estado,
          mes: fechaVencimiento.getMonth(),
          anio: fechaVencimiento.getFullYear(),
        });
      }
    });
    return cuotas;
  };

  // Efecto para activar búsqueda cuando el usuario ingresa criterios
  useEffect(() => {
    // Activar búsqueda si hay al menos 2 caracteres en searchTerm
    if (searchTerm.trim().length >= 2) {
      setShouldSearch(true);
    } else if (searchTerm.trim().length === 0) {
      // Limpiar resultados si borra la búsqueda
      setShouldSearch(false);
    }
  }, [searchTerm]);

  const cobros = generateCuotasAgrupadas();

  // Filtros adicionales (vendedor, estado, mes y año) - la búsqueda por lote/comprador ya se hizo en generateCuotasAgrupadas
  const filteredCobros = cobros.filter(cobro => {
    const mesActual = selectedMonth === 'Todos' ? null : months.indexOf(selectedMonth);

    // Filtrado por mes y año mejorado
    let matchesMonthYearFilter = true;
    if (selectedMonth === 'Todos') {
      // Si es "Todos" los meses, filtrar solo por año
      matchesMonthYearFilter = cobro.anio === selectedYear;
    } else {
      // Si es un mes específico, filtrar por mes Y año
      matchesMonthYearFilter = cobro.mes === mesActual && cobro.anio === selectedYear;
    }

    return (
      (selectedVendedor === 'Todos los vendedores' || cobro.vendedorNombre === selectedVendedor) &&
      (selectedEstado === 'Todos los estados' || cobro.estado === selectedEstado) &&
      matchesMonthYearFilter
    );
  }).reverse();

  // Calcular estadísticas reales
  // Solo sumar abonos sincronizados (los que marcan cuotas como pagadas)
  // Función robusta para obtener abonos sincronizados
  function getAbonosSincronizadosRobusto(lot: any, expenses: any[]): any[] {
    if (!lot.payments) return [];
    return lot.payments.filter((p: any) => {
      if (p.type !== 'abono') return false;
      return expenses.some((e: any) => {
        // Normalizar lotes a array
        let lotesExpense = e.lotes;
        if (typeof lotesExpense === 'string') lotesExpense = [lotesExpense];
        if (!Array.isArray(lotesExpense)) lotesExpense = [];
        // Tolerancia de tipo y formato
        const montoIgual = Math.abs(Number(e.monto) - Number(p.amount)) < 1;
        const tipoIngreso = e.tipo === 'ingreso';
        // Fecha tolerante: exacta o +/-1 día
        const fechaPago = new Date(p.date);
        const fechaExpense = new Date(e.fecha);
        const diffDias = Math.abs((fechaExpense.getTime() - fechaPago.getTime()) / (1000 * 60 * 60 * 24));
        const fechaIgual = diffDias <= 1;
        // Referencia: debe incluir el número de cuota o recibo
        const referenciaIncluye = (e.referencia && (p.cuotaNumero ? e.referencia.includes(p.cuotaNumero) : false)) || (e.referencia && p.receipt && e.referencia.includes(p.receipt));
        // Coincidencia de lote
        const loteCoincide = lotesExpense.includes(lot.id?.toString());
        // Log de depuración
        console.log('Comparando abono vs expense:', { montoIgual, tipoIngreso, fechaIgual, referenciaIncluye, loteCoincide, expense: e, abono: p });
        return montoIgual && tipoIngreso && fechaIgual && referenciaIncluye && loteCoincide;
      });
    });
  }
  const abonosSincronizados = lots.flatMap((lot: any) => getAbonosSincronizadosRobusto(lot, expenses));
  console.log('Abonos sincronizados para stats:', abonosSincronizados);
  const stats = {
    saldoPendiente: lots
      .filter((lot: any) => lot.status === 'crédito')
      .reduce((total: any, lot: any) => total + (lot.totalValue - lot.amountPaid), 0)
      .toFixed(2),
    totalCobrado: abonosSincronizados.reduce((total: any, abono: any) => total + Number(abono.amount), 0).toFixed(2),
    porcentajeAvance: lots.filter((lot: any) => lot.status === 'crédito').length > 0
      ? ((abonosSincronizados.reduce((total: any, abono: any) => total + Number(abono.amount), 0) /
        lots.filter((lot: any) => lot.status === 'crédito').reduce((total: any, lot: any) => total + lot.totalValue, 0)) * 100).toFixed(1)
      : '0'
  };

  // Calcular totales del mes actual
  // Eliminar las declaraciones previas de 'now', 'mesActual', 'anioActual' para evitar redeclaración
  // const now = new Date();
  // const mesActual = now.getMonth();
  // const anioActual = now.getFullYear();
  // --- AJUSTE: Calcular para el mes siguiente ---
  const mesProximo = (mesActual + 1) % 12;
  const anioProximo = mesActual === 11 ? anioActual + 1 : anioActual;
  // ---
  const abonosMes = abonosSincronizados.filter((a: any) => {
    const fecha = new Date(a.date);
    return fecha.getMonth() === mesProximo && fecha.getFullYear() === anioProximo;
  });
  const totalCobradoMes = abonosMes.reduce((sum: number, a: any) => sum + Number(a.amount), 0);
  const cuotasMes = cobros.filter((c: any) => {
    const fecha = new Date(c.fechaVencimiento);
    return fecha.getMonth() === mesProximo && fecha.getFullYear() === anioProximo;
  });
  const totalEsperadoMes = cuotasMes.reduce((sum: number, c: any) => sum + Number(c.montoCuota), 0);
  const porcentajeAvanceMes = totalEsperadoMes > 0 ? (totalCobradoMes / totalEsperadoMes) * 100 : 0;

  // Eliminar cálculo y visualización de la tarjeta de 'Cobrado este mes'

  const getEstadoBadge = (estado: string) => {
    const variants = {
      'Pendiente': 'destructive',
      'Al día': 'default',
      'Vencido': 'secondary'
    } as const;

    return (
      <Badge variant={variants[estado as keyof typeof variants] || 'default'}>
        {estado}
      </Badge>
    );
  };

  // En handleGuardarAbono, solo actualizar el lote y cuota correctos
  const handleGuardarAbono = async () => {
    if (!abonoForm.numeroDocumento.trim() || !abonoForm.fecha.trim()) {
      setError('Todos los campos obligatorios deben estar completos. El No. Doc. del Banco es obligatorio.');
      return;
    }
    if (selectedCobro) {
      const lotIds = selectedCobro.lotes.split(',').map((id: string) => id.trim());
      const cuotaNumero = String(selectedCobro.cuotaNumero || selectedCobro.cuota);
      const cuotaFechaVencimiento = normalizeDate(selectedCobro.cuotaFechaVencimiento || selectedCobro.fechaVencimiento);
      // Actualizar abono en cada lote en Firestore
      await Promise.all(lots.filter((lot: any) => lotIds.includes(lot.id)).map(async (lot: any) => {
        let pagoEditado = false;
        const pagos = (lot.payments || []).map((p: any) => {
          if (
            p.type === 'abono' &&
            String(p.cuotaNumero) === cuotaNumero &&
            normalizeDate(p.cuotaFechaVencimiento) === cuotaFechaVencimiento
          ) {
            pagoEditado = true;
            return {
              ...p,
              amount: parseFloat(selectedCobro.montoCuota),
              date: abonoForm.fecha,
              receipt: abonoForm.numeroDocumento || abonoForm.boleta, // Usar numeroDocumento como principal, boleta como fallback
              receiptNumber: abonoForm.numeroDocumento || abonoForm.boleta,
              depositSlip: abonoForm.transferencia,
              cuotaNumero,
              cuotaFechaVencimiento
            };
          }
          return p;
        });
        const nuevoPago = {
          amount: parseFloat(selectedCobro.montoCuota),
          date: abonoForm.fecha,
          type: 'abono',
          method: 'Abono',
          receipt: abonoForm.numeroDocumento,
          receiptNumber: abonoForm.numeroDocumento,
          depositSlip: abonoForm.transferencia,
          cuotaNumero,
          cuotaFechaVencimiento
        };
        const pagosFinal = pagoEditado ? pagos : [...pagos, nuevoPago];
        await updateLot(lot.id, {
          payments: pagosFinal,
          amountPaid: pagosFinal.filter((p: any) => p.type === 'abono').reduce((sum: number, p: any) => sum + Number(p.amount), 0)
        });
      }));
      // Actualizar/crear movimiento en Firestore
      const movimientoExistente = expenses.find((e: any) =>
        e.tipo === 'ingreso' &&
        e.lotes === selectedCobro.lotes &&
        e.referencia && e.referencia.includes(`Cuota: ${selectedCobro.cuota}`)
      );
      if (movimientoExistente) {
        // Construir referencia con información completa
        const referencia = abonoForm.transferencia?.trim()
          ? `No. Doc.: ${abonoForm.numeroDocumento.trim()}, No. Autorización: ${abonoForm.transferencia.trim()}, Cuota: ${selectedCobro.cuota}`
          : `No. Doc.: ${abonoForm.numeroDocumento.trim()}, Cuota: ${selectedCobro.cuota}`;

        await updateMovimiento(movimientoExistente.id, {
          fecha: abonoForm.fecha,
          monto: parseFloat(selectedCobro.montoCuota),
          referencia: referencia,
          estado: 'Pagado',
          // Actualizar campos para match con banco
          numeroDocumento: abonoForm.numeroDocumento.trim(),
          transferencia: abonoForm.transferencia?.trim() || null
        });
      } else {
        // Construir referencia con información completa
        const referencia = abonoForm.transferencia?.trim()
          ? `No. Doc.: ${abonoForm.numeroDocumento.trim()}, No. Autorización: ${abonoForm.transferencia.trim()}, Cuota: ${selectedCobro.cuota}`
          : `No. Doc.: ${abonoForm.numeroDocumento.trim()}, Cuota: ${selectedCobro.cuota}`;

        await addMovimiento({
          concepto: `Abono de ${selectedCobro.comprador} para lote ${selectedCobro.lotes} (Cuota ${selectedCobro.cuota})`,
          responsable: selectedCobro.vendedor,
          fecha: abonoForm.fecha,
          monto: parseFloat(selectedCobro.montoCuota),
          estado: 'Pagado',
          referencia: referencia,
          tipo: 'ingreso',
          lotes: selectedCobro.lotes,
          vendedor: selectedCobro.vendedor,
          automatic: true, // Marcar como movimiento automático
          // Nuevos campos para match con banco
          numeroDocumento: abonoForm.numeroDocumento.trim(),
          transferencia: abonoForm.transferencia?.trim() || null
        });
      }
      // Actualizar estado de la cuota en la subcolección 'cuotas' a 'pagado'
      try {
        const grupoId = selectedCobro.lotes;
        await updateDoc(
          doc(db, 'cobros', grupoId, 'cuotas', cuotaNumero),
          { estado: 'pagado', fechaPago: abonoForm.fecha }
        );
      } catch (e) {
        console.error('Error al actualizar el estado de la cuota:', e);
      }
    }
    setModalOpen(false);
    setSelectedCobro(null);
    setAbonoForm({ numeroDocumento: '', transferencia: '', boleta: '', fecha: '' });
    setError(null);
  };

  const handleUndoAbono = async (cobro: any) => {
    const lotIds = cobro.lotes.split(',').map((id: string) => id.trim());
    // Eliminar el abono de los payments de cada lote en Firestore
    await Promise.all(lots.filter((lot: any) => lotIds.includes(lot.id)).map(async (lot: any) => {
      const pagos = (lot.payments || []).filter((p: any) => {
        return !(
          p.type === 'abono' &&
          p.cuotaNumero === cobro.cuotaNumero &&
          p.cuotaFechaVencimiento === cobro.cuotaFechaVencimiento &&
          (p.receipt === cobro.recibo || p.receiptNumber === cobro.recibo || !cobro.recibo || cobro.recibo === '0')
        );
      });
      await updateLot(lot.id, {
        payments: pagos,
        amountPaid: pagos.filter((p: any) => p.type === 'abono').reduce((sum: number, p: any) => sum + Number(p.amount), 0)
      });
    }));

    // Eliminar el movimiento automático correspondiente en Firestore
    const movimiento = expenses.find((e: any) =>
      e.tipo === 'ingreso' &&
      e.lotes === cobro.lotes &&
      e.referencia && e.referencia.includes(`Cuota: ${cobro.cuota}`) &&
      (e.referencia.includes(`Boleta: ${cobro.recibo}`) || !cobro.recibo || cobro.recibo === '0') &&
      e.automatic // Solo eliminar movimientos automáticos
    );
    if (movimiento) {
      await deleteMovimiento(movimiento.id);
    }
    setModalOpen(false);
    setSelectedCobro(null);
    setAbonoForm({ numeroDocumento: '', transferencia: '', boleta: '', fecha: '' });
    setError(null);
  };

  // Utilidad para obtener la fecha local en formato YYYY-MM-DD en zona horaria de Centroamérica
  function getLocalDateYMD() {
    const now = new Date();
    // Ajustar a zona horaria de Guatemala (UTC-6)
    const offsetMs = now.getTimezoneOffset() * 60000;
    const local = new Date(now.getTime() - offsetMs);
    return local.toISOString().split('T')[0];
  }

  // Calcular cuotas pendientes para un lote
  // HOTFIX PRODUCCIÓN: Unificar lógica con Estado de Cuenta para resolver inconsistencia en estados de cuotas
  // Se valida tanto cuotaNumero como cuotaFechaVencimiento con fallback a lógica anterior por seguridad
  const calcularCuotasPendientes = (lot: any) => {
    const totalValue = lot.totalValue || 0;
    const amountPaid = lot.amountPaid || 0;
    const numberOfPayments = lot.numberOfPayments || lot.numeroPagos || 1;

    // Calcular enganche
    const enganche = (lot.payments || []).find((p: any) => p.type === 'enganche')?.amount || 0;
    const deudaTotal = totalValue - enganche;
    const cuotaMensual = deudaTotal / numberOfPayments;

    // Calcular estado de cada cuota individual
    const estadoCuotas = [];
    let cuotasPagadas = 0;
    let cuotasParciales = 0;
    let abonosPagados = 0;

    for (let i = 1; i <= numberOfPayments; i++) {
      // Calcular fecha de vencimiento para esta cuota
      const fechaVenta = new Date(lot.saleDate || new Date());
      const fechaVencimiento = new Date(fechaVenta);
      fechaVencimiento.setMonth(fechaVencimiento.getMonth() + i);
      const fechaVencimientoYMD = fechaVencimiento.toISOString().split('T')[0];

      // HOTFIX: Intentar lógica unificada con fallback a lógica anterior
      let abonosCuota = 0;
      try {
        // Buscar todos los abonos para esta cuota específica (lógica unificada como Estado de Cuenta)
        abonosCuota = (lot.payments || [])
          .filter((p: any) => {
            if (p.type !== 'abono') return false;
            const sameCuota = String(p.cuotaNumero) === String(i);
            // Tolerar diferencias de fecha de vencimiento o permitir sin fecha
            const pVenc = (p.cuotaFechaVencimiento || '').toString().slice(0, 10);
            const sameVenc = !p.cuotaFechaVencimiento || pVenc === fechaVencimientoYMD;
            return sameCuota && sameVenc;
          })
          .reduce((sum: number, p: any) => sum + Number(p.amount), 0);
      } catch (error) {
        // Fallback a lógica anterior si falla
        console.warn('Hotfix fallback: usando lógica anterior para cálculo de cuotas', error);
        abonosCuota = (lot.payments || [])
          .filter((p: any) => p.type === 'abono' && p.cuotaNumero === i)
          .reduce((sum: number, p: any) => sum + Number(p.amount), 0);
      }

      if (abonosCuota >= cuotaMensual) {
        // Cuota pagada completamente
        estadoCuotas.push({ numero: i, estado: 'pagada', montoPagado: abonosCuota, pendiente: 0 });
        cuotasPagadas++;
        abonosPagados += cuotaMensual;
      } else if (abonosCuota > 0) {
        // Cuota con pago parcial
        estadoCuotas.push({ numero: i, estado: 'parcial', montoPagado: abonosCuota, pendiente: cuotaMensual - abonosCuota });
        cuotasParciales++;
        abonosPagados += abonosCuota;
      } else {
        // Cuota sin pagos
        estadoCuotas.push({ numero: i, estado: 'pendiente', montoPagado: 0, pendiente: cuotaMensual });
      }
    }

    const cuotasPendientes = numberOfPayments - cuotasPagadas;
    const saldoPendiente = deudaTotal - abonosPagados;

    return {
      cuotaMensual,
      cuotasPagadas,
      cuotasParciales,
      cuotasPendientes,
      saldoPendiente,
      abonosPagados,
      deudaTotal,
      enganche,
      estadoCuotas
    };
  };

  // Calcular aplicación del abono manual - CORREGIDO para evitar saldos negativos absurdos
  const calcularAplicacionAbonoManual = (lot: any, montoAbono: number) => {
    const calculo = calcularCuotasPendientes(lot);
    const cuotaMensual = calculo.cuotaMensual;
    const saldoPendiente = calculo.saldoPendiente;
    const estadoCuotas = calculo.estadoCuotas;

    let montoRestante = montoAbono;
    let cuotasAfectadas: number[] = [];
    let excedente = 0;
    let montoAplicadoACuotas = 0;

    // Aplicar el abono a las cuotas pendientes en orden
    for (let i = 0; i < estadoCuotas.length && montoRestante > 0; i++) {
      const cuota = estadoCuotas[i];

      if (cuota.estado === 'pendiente') {
        // Cuota sin pagos, aplicar monto completo
        if (montoRestante >= cuotaMensual) {
          cuotasAfectadas.push(cuota.numero);
          montoAplicadoACuotas += cuotaMensual;
          montoRestante -= cuotaMensual;
        } else {
          // Pago parcial de la cuota
          cuotasAfectadas.push(cuota.numero);
          excedente = montoRestante;
          montoAplicadoACuotas += montoRestante;
          montoRestante = 0;
        }
      } else if (cuota.estado === 'parcial') {
        // Cuota con pago parcial, completar primero
        const montoNecesario = cuota.pendiente;
        if (montoRestante >= montoNecesario) {
          cuotasAfectadas.push(cuota.numero);
          montoAplicadoACuotas += montoNecesario;
          montoRestante -= montoNecesario;
        } else {
          // Pago parcial para completar la cuota
          cuotasAfectadas.push(cuota.numero);
          excedente = montoRestante;
          montoAplicadoACuotas += montoRestante;
          montoRestante = 0;
        }
      }
      // Si la cuota está 'pagada', saltar a la siguiente
    }

    // CORRECCIÓN CRÍTICA: Validar que el saldo no sea negativo absurdo
    // Si el abono es mayor al saldo pendiente, el saldo después debe ser 0
    const saldoDespuesCalculado = saldoPendiente - montoAbono;
    const saldoDespues = Math.max(0, saldoDespuesCalculado);

    // Calcular cuotas restantes correctamente
    const cuotasCompletamenteAfectadas = cuotasAfectadas.filter((_, idx) => {
      // Solo contar cuotas que fueron completamente pagadas (no parciales)
      return idx < cuotasAfectadas.length - 1 || excedente === 0;
    }).length;

    return {
      cuotaAplicada: montoAplicadoACuotas,
      excedente,
      cuotasFuturasAfectadas: cuotasAfectadas,
      saldoDespues, // Ahora garantiza >= 0
      cuotasRestantes: Math.max(0, calculo.cuotasPendientes - cuotasCompletamenteAfectadas)
    };
  };

  // Normalizador de fecha a YYYY-MM-DD
  function normalizeDate(dateStr: string) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toISOString().split('T')[0];
  }

  // Formatear fecha a DD/MM/YYYY para mostrar en la UI
  function formatGTDate(dateStr: string) {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('es-GT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  // Calcular preview cuando cambia el monto del abono manual
  React.useEffect(() => {
    if (selectedLotManual && abonoManualForm.monto) {
      const monto = parseFloat(abonoManualForm.monto);
      if (!isNaN(monto) && monto > 0) {
        const calculo = calcularAplicacionAbonoManual(selectedLotManual, monto);
        setPreviewCalculoManual(calculo);
      } else {
        setPreviewCalculoManual(null);
      }
    }
  }, [selectedLotManual, abonoManualForm.monto]);

  // Manejar registro de abono manual (soporta múltiples lotes)
  const handleRegistrarAbonoManual = async () => {
    // Validación inicial
    if (!abonoManualForm.numeroDocumento || !abonoManualForm.fecha) {
      setError('Todos los campos obligatorios deben estar completos. El No. Doc. del Banco es obligatorio.');
      return;
    }

    // Determinar si es modo múltiple o modo legacy (un solo lote)
    const esModoMultiple = selectedLotesManual.length > 0;

    if (esModoMultiple) {
      // Validar que todos los lotes tengan monto válido
      const lotesInvalidos = selectedLotesManual.filter(item => {
        const monto = parseFloat(item.monto);
        return !item.monto || isNaN(monto) || monto <= 0;
      });

      if (lotesInvalidos.length > 0) {
        setError('Todos los lotes deben tener un monto válido mayor a 0');
        return;
      }
    } else {
      // Modo legacy: validar lote único
      if (!selectedLotManual || !abonoManualForm.monto) {
        setError('Todos los campos obligatorios deben estar completos.');
        return;
      }

      const monto = parseFloat(abonoManualForm.monto);
      if (isNaN(monto) || monto <= 0) {
        setError('El monto debe ser un número válido mayor a 0');
        return;
      }
    }

    try {
      const now = new Date().toISOString();

      // Función auxiliar para procesar un solo lote
      const procesarAbonoLote = async (lot: any, montoLote: number) => {
        const calculo = calcularAplicacionAbonoManual(lot, montoLote);

        // 1. Crear abonos específicos para cada cuota afectada
        const abonosCuotas = [];
        const calculoOriginal = calcularCuotasPendientes(lot);
        const cuotaMensual = calculoOriginal.cuotaMensual;
        const estadoCuotas = calculoOriginal.estadoCuotas;

        // Crear abonos basados en el cálculo inteligente
        let montoRestante = montoLote;

        // Aplicar a cuotas pendientes en orden, considerando pagos parciales existentes
        for (let i = 0; i < estadoCuotas.length && montoRestante > 0; i++) {
          const cuota = estadoCuotas[i];

          if (cuota.estado === 'pendiente') {
            // Cuota sin pagos - aplicar monto completo
            if (montoRestante >= cuotaMensual) {
              const fechaVenta = new Date(lot.saleDate || lot.fechaVenta || new Date());
              const fechaVencimiento = new Date(fechaVenta);
              fechaVencimiento.setMonth(fechaVenta.getMonth() + cuota.numero);
              const fechaVencimientoYMD = fechaVencimiento.toISOString().split('T')[0];

              abonosCuotas.push({
                amount: cuotaMensual,
                date: abonoManualForm.fecha,
                type: 'abono',
                method: 'Abono Manual',
                receipt: abonoManualForm.numeroDocumento || abonoManualForm.boleta,
                receiptNumber: abonoManualForm.numeroDocumento || abonoManualForm.boleta,
                depositSlip: abonoManualForm.transferencia,
                cuotaNumero: cuota.numero,
                cuotaFechaVencimiento: fechaVencimientoYMD,
                observaciones: `Abono manual - ${abonoManualForm.observaciones || 'Sin observaciones'}`
              });

              montoRestante -= cuotaMensual;
            } else {
              // Pago parcial de la cuota
              const fechaVenta = new Date(lot.saleDate || lot.fechaVenta || new Date());
              const fechaVencimiento = new Date(fechaVenta);
              fechaVencimiento.setMonth(fechaVenta.getMonth() + cuota.numero);
              const fechaVencimientoYMD = fechaVencimiento.toISOString().split('T')[0];

              abonosCuotas.push({
                amount: montoRestante,
                date: abonoManualForm.fecha,
                type: 'abono',
                method: 'Abono Manual',
                receipt: abonoManualForm.numeroDocumento || abonoManualForm.boleta,
                receiptNumber: abonoManualForm.numeroDocumento || abonoManualForm.boleta,
                depositSlip: abonoManualForm.transferencia,
                cuotaNumero: cuota.numero,
                cuotaFechaVencimiento: fechaVencimientoYMD,
                observaciones: `Abono manual parcial - ${abonoManualForm.observaciones || 'Sin observaciones'}`
              });

              montoRestante = 0;
            }
          } else if (cuota.estado === 'parcial') {
            // Cuota con pago parcial - SOLO completar lo que falta
            const montoNecesario = cuota.pendiente;

            if (montoRestante >= montoNecesario) {
              // Completar la cuota parcial
              const fechaVenta = new Date(lot.saleDate || lot.fechaVenta || new Date());
              const fechaVencimiento = new Date(fechaVenta);
              fechaVencimiento.setMonth(fechaVenta.getMonth() + cuota.numero);
              const fechaVencimientoYMD = fechaVencimiento.toISOString().split('T')[0];

              abonosCuotas.push({
                amount: montoNecesario,
                date: abonoManualForm.fecha,
                type: 'abono',
                method: 'Abono Manual',
                receipt: abonoManualForm.numeroDocumento || abonoManualForm.boleta,
                receiptNumber: abonoManualForm.numeroDocumento || abonoManualForm.boleta,
                depositSlip: abonoManualForm.transferencia,
                cuotaNumero: cuota.numero,
                cuotaFechaVencimiento: fechaVencimientoYMD,
                observaciones: `Abono manual completar cuota - ${abonoManualForm.observaciones || 'Sin observaciones'}`
              });

              montoRestante -= montoNecesario;
            } else {
              // Pago parcial para completar la cuota
              const fechaVenta = new Date(lot.saleDate || lot.fechaVenta || new Date());
              const fechaVencimiento = new Date(fechaVenta);
              fechaVencimiento.setMonth(fechaVenta.getMonth() + cuota.numero);
              const fechaVencimientoYMD = fechaVencimiento.toISOString().split('T')[0];

              abonosCuotas.push({
                amount: montoRestante,
                date: abonoManualForm.fecha,
                type: 'abono',
                method: 'Abono Manual',
                receipt: abonoManualForm.numeroDocumento || abonoManualForm.boleta,
                receiptNumber: abonoManualForm.numeroDocumento || abonoManualForm.boleta,
                depositSlip: abonoManualForm.transferencia,
                cuotaNumero: cuota.numero,
                cuotaFechaVencimiento: fechaVencimientoYMD,
                observaciones: `Abono manual completar cuota parcial - ${abonoManualForm.observaciones || 'Sin observaciones'}`
              });

              montoRestante = 0;
            }
          }
        }

        if (montoRestante > 0) {
          console.warn(`⚠️ ADVERTENCIA: Queda monto sin aplicar para lote ${lot.id}:`, montoRestante);
        }

        // 2. Actualizar lote con todos los abonos
        const pagosActualizados = [...(lot.payments || []), ...abonosCuotas];
        const nuevoAmountPaid = (lot.amountPaid || 0) + montoLote;

        await updateLot(lot.id, {
          payments: pagosActualizados,
          amountPaid: nuevoAmountPaid
        });

        // 3. Obtener todas las cuotas afectadas para la referencia
        const cuotasAfectadas = abonosCuotas.map(abono => abono.cuotaNumero).sort((a, b) => a - b);
        const cuotasTexto = cuotasAfectadas.length > 0
          ? `Cuotas: ${cuotasAfectadas.join(', ')}`
          : 'Sin cuotas específicas';

        // Construir referencia con información completa
        const referencia = abonoManualForm.transferencia?.trim()
          ? `No. Doc.: ${abonoManualForm.numeroDocumento.trim()}, No. Autorización: ${abonoManualForm.transferencia.trim()}, ${cuotasTexto}`
          : `No. Doc.: ${abonoManualForm.numeroDocumento.trim()}, ${cuotasTexto}`;

        // Construir concepto con las cuotas afectadas claramente visibles
        const cuotasTextoConcepto = cuotasAfectadas.length > 0
          ? ` (Cuotas ${cuotasAfectadas.join(', ')})`
          : '';

        // Detectar si es grupo de lotes
        const esGrupo = Array.isArray(lot.idLotes) && lot.idLotes.length > 1;
        const lotIds = esGrupo ? lot.idLotes : [lot.id];
        const concepto = `Abono manual de ${lot.buyerName} para ${esGrupo ? `lotes ${lotIds.join(', ')}` : `lote ${lot.id}`}${cuotasTextoConcepto}`;

        // Preparar datos del movimiento sin campos undefined
        const movimientoData: any = {
          concepto: concepto,
          responsable: lot.seller,
          fecha: abonoManualForm.fecha,
          monto: montoLote,
          estado: 'Pagado',
          referencia: referencia,
          tipo: 'ingreso',
          lotes: lotIds,
          comprador: lot.buyerName,
          vendedor: lot.seller,
          automatic: true,
          numeroDocumento: abonoManualForm.numeroDocumento.trim(),
          cuotasAfectadas: cuotasAfectadas,
          abonosCuotas: abonosCuotas.map(abono => ({
            cuotaNumero: abono.cuotaNumero,
            monto: abono.amount,
            fechaVencimiento: abono.cuotaFechaVencimiento
          }))
        };

        // Solo agregar transferencia si tiene valor (Firestore no permite undefined)
        if (abonoManualForm.transferencia?.trim()) {
          movimientoData.transferencia = abonoManualForm.transferencia.trim();
        }

        await addMovimiento(movimientoData);

        return { lot, montoLote, abonosCuotas, calculo };
      };

      // Procesar según el modo (múltiple o legacy)
      if (esModoMultiple) {
        // Modo múltiple: procesar cada lote y crear un movimiento por cada uno
        const resultados = [];
        for (const item of selectedLotesManual) {
          const montoLote = parseFloat(item.monto);
          const resultado = await procesarAbonoLote(item.lot, montoLote);
          resultados.push(resultado);
        }

        const totalLotes = resultados.length;
        const totalMonto = resultados.reduce((sum, r) => sum + r.montoLote, 0);

        toast.success(`Se registraron ${totalLotes} abono(s) manual(es) con un total de Q. ${totalMonto.toLocaleString(undefined, { minimumFractionDigits: 2 })}. Todos comparten el No. Doc. ${abonoManualForm.numeroDocumento}.`);
      } else {
        // Modo legacy: procesar un solo lote
        const monto = parseFloat(abonoManualForm.monto);
        const resultado = await procesarAbonoLote(selectedLotManual, monto);

        // Guardar registro de abono manual (opcional, no crítico si falla)
        try {
          const registroId = `abono_manual_${selectedLotManual.id}_${Date.now()}`;
          const registroData: any = {
            lotId: selectedLotManual.id,
            comprador: selectedLotManual.buyerName,
            monto,
            fecha: abonoManualForm.fecha,
            numeroDocumento: abonoManualForm.numeroDocumento,
            observaciones: abonoManualForm.observaciones || '',
            cuotaAplicada: resultado.calculo.cuotaAplicada,
            excedente: resultado.calculo.excedente,
            cuotasFuturasAfectadas: resultado.calculo.cuotasFuturasAfectadas,
            abonosCuotas: resultado.abonosCuotas,
            createdAt: now
          };

          if (abonoManualForm.transferencia?.trim()) {
            registroData.transferencia = abonoManualForm.transferencia.trim();
          }

          await setDoc(doc(db, 'abonosManuales', registroId), registroData);
        } catch (registroError: any) {
          console.warn('Error al guardar registro de abono manual (no crítico):', registroError);
        }

        const mensaje = resultado.calculo.cuotasFuturasAfectadas.length > 0
          ? `Abono manual registrado exitosamente. Se aplicaron ${resultado.calculo.cuotasFuturasAfectadas.length} cuotas futuras.`
          : `Abono manual registrado exitosamente. Se aplicó a la cuota actual.`;

        toast.success(mensaje);
      }

      // Limpiar formulario
      setModalAbonoManualOpen(false);
      setSelectedLotManual(null);
      setSelectedLotesManual([]);
      setAbonoManualForm({
        monto: '',
        fecha: new Date().toISOString().split('T')[0],
        numeroDocumento: '',
        transferencia: '',
        boleta: '',
        observaciones: ''
      });
      setPreviewCalculoManual(null);
      setError(null);

    } catch (error: any) {
      console.error('Error al registrar abono manual:', error);
      const errorMessage = error?.message || 'Error desconocido';
      console.error('Detalles del error:', errorMessage);
      setError(`Error al registrar el abono: ${errorMessage}. Inténtalo de nuevo.`);
    }
  };

  // Copiar la función utilitaria aquí si no se puede importar
  function getAbonosSincronizados(lote: any, expenses: any[]): any[] {
    if (!lote.payments) return [];
    return lote.payments.filter((p: any) => p.type === 'abono' && expenses.some((e: any) => {
      const montoIgual = parseFloat(e.monto) === parseFloat(p.amount);
      const fechaIgual = normalizeDate(e.fecha) === normalizeDate(p.date);
      const tipoIgual = e.tipo === 'abono';
      const cuotaIgual = (e.cuotaNumero ? e.cuotaNumero.toString() : '') === (p.cuotaNumero ? p.cuotaNumero.toString() : '');
      // Normalizar fecha de vencimiento
      const vencimientoIgual = normalizeDate(e.cuotaFechaVencimiento || e.fechaVencimiento) === normalizeDate(p.cuotaFechaVencimiento || p.fechaVencimiento);
      const reciboIgual = (e.referencia && p.receipt) ? e.referencia.includes(p.receipt) : true;
      return montoIgual && fechaIgual && tipoIgual && cuotaIgual && vencimientoIgual && reciboIgual;
    }));
  }

  // Funciones de exportación para Cobros
  const exportCobrosToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredCobros.map(c => ({
      'Comprador': c.comprador,
      'Vendedor': c.vendedorNombre,
      'Lotes': c.lotes,
      'Recibo': c.recibo,
      'Fecha Venta': c.fechaVenta,
      'Cuota': c.cuota,
      'Fecha Vencimiento': c.fechaVencimiento,
      'Monto Cuota': c.montoCuota,
      'Estado': c.estado
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cobros');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    FileSaver.saveAs(new Blob([wbout], { type: 'application/octet-stream' }), 'cobros.xlsx');
  };
  const exportCobrosToCSV = () => {
    const ws = XLSX.utils.json_to_sheet(filteredCobros.map(c => ({
      'Comprador': c.comprador,
      'Vendedor': c.vendedorNombre,
      'Lotes': c.lotes,
      'Recibo': c.recibo,
      'Fecha Venta': c.fechaVenta,
      'Cuota': c.cuota,
      'Fecha Vencimiento': c.fechaVencimiento,
      'Monto Cuota': c.montoCuota,
      'Estado': c.estado
    })));
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    FileSaver.saveAs(blob, 'cobros.csv');
  };
  const exportCobrosToPDF = () => {
    const doc = new jsPDF();
    doc.text('Cobros', 14, 16);
    autoTable(doc, {
      head: [[
        'Comprador', 'Vendedor', 'Lotes', 'Recibo', 'Fecha Venta', 'Cuota', 'Fecha Vencimiento', 'Monto Cuota', 'Estado'
      ]],
      body: filteredCobros.map(c => [
        c.comprador, c.vendedorNombre, c.lotes, c.recibo, c.fechaVenta, c.cuota, c.fechaVencimiento, c.montoCuota, c.estado
      ]),
      startY: 22,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [22, 160, 133] },
    });
    doc.save('cobros.pdf');
  };



  // Total cobrado: suma de cuotas filtradas con estado 'Al día'
  const totalCobradoFiltrado = filteredCobros
    .filter(c => c.estado === 'Al día')
    .reduce((sum, c) => sum + (parseFloat(c.montoCuota) || 0), 0);
  // Total a cobrar: suma de cuotas filtradas que NO están 'Al día'
  const totalACobrarFiltrado = filteredCobros
    .filter(c => c.estado !== 'Al día')
    .reduce((sum, c) => sum + (parseFloat(c.montoCuota) || 0), 0);
  // Porcentaje de avance: basado en los registros filtrados
  const porcentajeAvanceFiltrado = (totalCobradoFiltrado + totalACobrarFiltrado) > 0
    ? (totalCobradoFiltrado / (totalCobradoFiltrado + totalACobrarFiltrado)) * 100
    : 0;

  // Calcular avance general del proyecto basado en cuotas (independiente de filtros)
  let totalCuotas = 0;
  let cuotasPagadas = 0;

  lots.filter((lot: any) => lot.status === 'crédito' && lot.paymentType === 'pagos').forEach((lot: any) => {
    const numberOfPayments = lot.numberOfPayments || lot.numeroPagos || 1;
    totalCuotas += numberOfPayments;

    // Contar cuotas pagadas usando la función robusta
    const abonosSincronizados = getAbonosSincronizadosRobusto(lot, expenses);
    cuotasPagadas += abonosSincronizados.length;
  });

  const porcentajeAvanceGeneral = totalCuotas > 0 ? (cuotasPagadas / totalCuotas) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-foreground">Sistema de Cobros</h2>
          <p className="text-muted-foreground mt-1 text-[13px]">Gestiona los pagos y cuotas de los lotes vendidos</p>
        </div>
        <Button
          onClick={() => setModalAbonoManualOpen(true)}
          className="bg-transparent border border-white/10 hover:bg-white/5 text-foreground rounded-[6px] h-8 text-[13px] font-medium transition-colors px-3"
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          Registrar Abono Manual
        </Button>
      </div>

      {/* Filtros de Búsqueda */}
      <div className="flex flex-wrap items-center gap-3 mb-6 px-1">
        {/* Buscar */}
        <div className="relative w-64">
          <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/70" />
          <Input
            placeholder="Buscar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 bg-transparent border border-white/10 hover:border-white/20 focus:border-white/20 rounded-[6px] h-8 text-[13px] text-foreground placeholder:text-muted-foreground/40 shadow-none transition-colors"
          />
        </div>

        {/* Vendedor */}
        <div className="relative">
          <select
            value={selectedVendedor}
            onChange={e => setSelectedVendedor(e.target.value)}
            className="bg-transparent border border-white/10 hover:border-white/20 focus:border-white/20 rounded-[6px] h-8 text-[13px] text-foreground shadow-none transition-colors pl-3 pr-8 appearance-none cursor-pointer min-w-[150px]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%238A8F98' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 0.5rem center',
              backgroundSize: '10px'
            }}
          >
            {vendedores.map((vendedor) => (
              <option key={vendedor} value={vendedor}>{vendedor}</option>
            ))}
          </select>
        </div>

        {/* Estado */}
        <div className="relative">
          <select
            value={selectedEstado}
            onChange={e => setSelectedEstado(e.target.value)}
            className="bg-transparent border border-white/10 hover:border-white/20 focus:border-white/20 rounded-[6px] h-8 text-[13px] text-foreground shadow-none transition-colors pl-3 pr-8 appearance-none cursor-pointer min-w-[130px]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%238A8F98' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 0.5rem center',
              backgroundSize: '10px'
            }}
          >
            {estados.map((estado) => (
              <option key={estado} value={estado}>{estado}</option>
            ))}
          </select>
        </div>

        <div className="flex-1" />

        {/* Exportar */}
        <TableExportMenu
          onExcel={exportCobrosToExcel}
          onCSV={exportCobrosToCSV}
          onPDF={exportCobrosToPDF}
        />
      </div>
      {/* Período de Consulta - usar TablePeriodSelector sin card wrapper */}
      <TablePeriodSelector
        selectedMonth={selectedMonth}
        selectedYear={selectedYear}
        onMonthChange={setSelectedMonth}
        onYearChange={setSelectedYear}
        months={months}
        label="Período de Consulta"
      />
      {/* Grid de cards de totales */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="bg-card border border-amber-500/20 shadow-none rounded-lg">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total a cobrar</CardTitle>
            <DollarSign className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-amber-500">Q. {totalACobrarFiltrado.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
          </CardContent>
        </Card>
        <Card className="bg-card border border-emerald-500/20 shadow-none rounded-lg">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total cobrado</CardTitle>
            <DollarSign className="h-4 w-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-emerald-500">Q. {totalCobradoFiltrado.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
          </CardContent>
        </Card>
        <Card className="bg-card border border-blue-500/20 shadow-none rounded-lg">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Porcentaje de avance</CardTitle>
            <TrendingUp className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-500">{porcentajeAvanceFiltrado.toFixed(1)}%</div>
          </CardContent>
        </Card>
        {/* Tarjeta: Avance general del proyecto (última) */}
        <Card className="bg-card border border-violet-500/20 shadow-none rounded-lg">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Avance General</CardTitle>
            <TrendingUp className="h-4 w-4 text-violet-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-violet-500">{porcentajeAvanceGeneral.toFixed(1)}%</div>
            <div className="text-xs text-muted-foreground mt-1">{cuotasPagadas} / {totalCuotas} cuotas pagadas</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabla de Cobros */}
      <Card className="bg-card border-border shadow-none rounded-lg overflow-hidden">
        <CardHeader className="pb-3 pt-4 px-4 border-b border-border/50">
          <div className="flex justify-between items-center">
            <CardTitle className="text-sm font-medium tracking-tight">Cobros ({filteredCobros.length})</CardTitle>
            <span className="text-xs text-muted-foreground">Mostrando {filteredCobros.length} de {cobros.length} registros</span>
          </div>
        </CardHeader>
        <CardContent className="bg-card p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-b border-white/5">
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Comprador</TableHead>
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Vendedor</TableHead>
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Lotes</TableHead>
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Recibo</TableHead>
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Fecha Venta</TableHead>
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Cuota</TableHead>
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Fecha Vencimiento</TableHead>
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Monto Cuota</TableHead>
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Estado</TableHead>
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCobros.map((cobro, index) => {
                  // Determinar si ya hay abono registrado para esta cuota de grupo
                  const lotIds = cobro.lotes.split(',').map((id: string) => id.trim());
                  // Usar la nueva lógica de calcularCuotasPendientes para determinar el estado real
                  const lotesGrupo = lots.filter((lot: any) => lotIds.includes(lot.id));
                  let abonoRegistrado = false;

                  // Verificar el estado real de la cuota usando la nueva lógica
                  if (lotesGrupo.length > 0) {
                    const lot = lotesGrupo[0]; // Usar el primer lote del grupo como referencia
                    const calculoCuotas = calcularCuotasPendientes(lot);
                    const numeroCuota = parseInt(cobro.cuota);

                    // Buscar la cuota específica en el estado calculado
                    const cuotaEstado = calculoCuotas.estadoCuotas.find((c: any) => c.numero === numeroCuota);

                    if (cuotaEstado) {
                      // La cuota está pagada si su estado es 'pagada' o si tiene pago parcial suficiente
                      abonoRegistrado = cuotaEstado.estado === 'pagada' ||
                        (cuotaEstado.estado === 'parcial' && cuotaEstado.montoPagado >= parseFloat(cobro.montoCuota));
                    }
                  }

                  // Calcular valor total del grupo de lotes
                  const valorTotalGrupo = lots.filter((lot: any) => lotIds.includes(lot.id)).reduce((sum: number, lot: any) => sum + (lot.totalValue || 0), 0);
                  // Calcular enganche inicial del grupo
                  const engancheGrupo = lots.filter((lot: any) => lotIds.includes(lot.id)).reduce((sum: number, lot: any) => {
                    const pagoEnganche = (lot.payments || []).find((p: any) => p.type === 'enganche');
                    return sum + (pagoEnganche ? Number(pagoEnganche.amount) : 0);
                  }, 0);
                  // Calcular total de abonos (enganche + abonos)
                  const totalAbonos = engancheGrupo + (lotesGrupo.length > 0 ? calcularCuotasPendientes(lotesGrupo[0]).abonosPagados : 0);
                  // Calcular deuda total (valor total - enganche)
                  const deudaTotal = valorTotalGrupo - engancheGrupo;
                  return (
                    <TableRow key={index} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                      <TableCell className="px-3 py-3 font-medium text-[13px]">{cobro.comprador}</TableCell>
                      <TableCell className="px-3 py-3 text-[13px]">{cobro.vendedorNombre}</TableCell>
                      <TableCell className="px-3 py-3 font-mono text-[13px]">{cobro.lotes}</TableCell>
                      <TableCell className="px-3 py-3 font-mono text-[13px]">{cobro.recibo}</TableCell>
                      <TableCell className="px-3 py-3 text-[13px]">{formatGTDate(cobro.fechaVenta)}</TableCell>
                      <TableCell className="px-3 py-3 text-center text-[13px]">{cobro.cuota}</TableCell>
                      <TableCell className="px-3 py-3 text-[13px]">{formatGTDate(cobro.fechaVencimiento)}</TableCell>
                      <TableCell className="px-3 py-3 font-mono text-[13px] text-emerald-500">Q. {parseFloat(cobro.montoCuota).toLocaleString()}</TableCell>
                      <TableCell className="px-3 py-3">
                        {cobro.estado === 'Al día' && (
                          <div className="flex items-center gap-1.5 text-emerald-500">
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            <span className="text-[13px] font-medium">Al día</span>
                          </div>
                        )}
                        {cobro.estado === 'Pendiente' && (
                          <div className="flex items-center gap-1.5 text-amber-500">
                            <Clock className="h-3.5 w-3.5" />
                            <span className="text-[13px] font-medium">Pendiente</span>
                          </div>
                        )}
                        {cobro.estado === 'Pendiente Parcial' && (
                          <div className="flex items-center gap-1.5 text-blue-500">
                            <Clock className="h-3.5 w-3.5" />
                            <span className="text-[13px] font-medium">Parcial</span>
                          </div>
                        )}
                        {cobro.estado === 'Vencido' && (
                          <div className="flex items-center gap-1.5 text-rose-500">
                            <AlertCircle className="h-3.5 w-3.5" />
                            <span className="text-[13px] font-medium">Vencido</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {abonoRegistrado ? (
                          <div className="flex gap-2">
                            {/* Botón de descarga de recibo */}
                            {(() => {
                              const lot = lotesGrupo[0];
                              if (!lot) return null;
                              const numeroCuota = parseInt(cobro.cuota);
                              const abonosCuota = (lot.payments || [])
                                .filter((p: any) => p.type === 'abono' && p.cuotaNumero === numeroCuota);
                              if (abonosCuota.length === 0) return null;

                              const abono = abonosCuota[0];
                              const calculoCuotas = calcularCuotasPendientes(lot);

                              return (
                                <DownloadReceiptButton
                                  empresaInfo={{
                                    razonSocial: config.razonSocial || 'Bimobi',
                                    nit: config.nit,
                                    direccionCompleta: config.direccionCompleta || config.ubicacion || 'Guatemala',
                                    telefono1: config.telefono1 || '',
                                    telefono2: config.telefono2,
                                    email: config.email,
                                    logoUrl: config.logoUrl
                                  }}
                                  detalleRecibo={{
                                    tipoRecibo: 'abono',
                                    cliente: {
                                      nombre: lot.buyerName || cobro.comprador || 'Sin nombre',
                                      dpi: lot.buyerDPI,
                                      telefono: lot.phoneNumber
                                    },
                                    lotes: cobro.lotes || '-',
                                    cuotaNumero: numeroCuota,
                                    fechaVencimiento: cobro.fechaVencimiento,
                                    montoAbono: parseFloat(abono.amount || 0),
                                    fechaPago: abono.date || '',
                                    transferencia: abono.depositSlip || '-',
                                    boleta: abono.receiptNumber || abono.receipt || '-',
                                    saldoPendiente: calculoCuotas.saldoPendiente
                                  } as Omit<DetalleAbono, 'numeroRecibo' | 'fechaEmision'>}
                                  fileName={`Recibo-Abono-${cobro.lotes}-Cuota${numeroCuota}`}
                                  onSuccess={(numeroRecibo) => {
                                    console.log('Recibo de abono generado:', numeroRecibo);
                                  }}
                                />
                              );
                            })()}

                            <Button variant="outline" size="icon" title="Editar abono" onClick={() => {
                              // Buscar el pago de abono correspondiente a esta cuota de grupo
                              if (lotesGrupo.length > 0) {
                                const lot = lotesGrupo[0];
                                const calculoCuotas = calcularCuotasPendientes(lot);
                                const numeroCuota = parseInt(cobro.cuota);

                                // Buscar abonos existentes para esta cuota específica
                                const abonosCuota = (lot.payments || [])
                                  .filter((p: any) => p.type === 'abono' && p.cuotaNumero === numeroCuota);

                                if (abonosCuota.length > 0) {
                                  const abono = abonosCuota[0]; // Usar el primer abono encontrado
                                  // Intentar extraer numeroDocumento del movimiento correspondiente
                                  const movimientoAbono = expenses.find((e: any) =>
                                    e.tipo === 'ingreso' &&
                                    e.lotes === cobro.lotes &&
                                    e.referencia && e.referencia.includes(`Cuota: ${numeroCuota}`) &&
                                    (e.referencia?.includes(`No. Doc.:`) || e.referencia?.includes(`Transferencia: ${abono.depositSlip}`))
                                  );
                                  setAbonoForm({
                                    numeroDocumento: movimientoAbono?.numeroDocumento || abono.receiptNumber || abono.receipt || '',
                                    transferencia: abono.depositSlip || '',
                                    boleta: abono.receipt || abono.receiptNumber || '', // Preservar valor histórico si existe
                                    fecha: abono.date || getLocalDateYMD()
                                  });
                                } else {
                                  setAbonoForm({ numeroDocumento: '', transferencia: '', boleta: '', fecha: '' });
                                }
                              } else {
                                setAbonoForm({ numeroDocumento: '', transferencia: '', boleta: '', fecha: '' });
                              }
                              setSelectedCobro(cobro);
                              setModalOpen(true);
                            }}>
                              <Pencil className="h-4 w-4 text-blue-600" />
                            </Button>
                            <Button variant="outline" size="icon" title="Eliminar abono" onClick={() => {
                              if (window.confirm('¿Estás seguro de que deseas eliminar este abono? Esta acción no se puede revertir.')) {
                                handleUndoAbono(cobro);
                              }
                            }}>
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                        ) : (
                          <Button variant="outline" size="sm" onClick={() => {
                            setSelectedCobro(cobro);
                            setAbonoForm(f => ({
                              numeroDocumento: '',
                              transferencia: '',
                              boleta: '',
                              fecha: f.fecha || getLocalDateYMD()
                            }));
                            setModalOpen(true);
                          }}>
                            Registrar abono
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {!shouldSearch && (
            <div className="text-center py-16">
              <Search className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Ingresa un criterio de búsqueda</h3>
              <p className="text-gray-500 mb-4">
                Busca por <strong>número de lote</strong> o <strong>nombre del comprador</strong>
              </p>
              <div className="text-sm text-gray-400 max-w-md mx-auto">
                <p>Ejemplos:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Lote: "A-15", "B-8", "100"</li>
                  <li>Comprador: "Juan", "María López"</li>
                </ul>
              </div>
            </div>
          )}

          {shouldSearch && filteredCobros.length === 0 && (
            <div className="text-center py-12">
              <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No se encontraron cuotas que coincidan con los filtros</p>
              <p className="text-gray-400 text-sm mt-2">Intenta con otro criterio de búsqueda</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal para registrar abono - Estilo Notion */}
      {modalOpen && (
        <Dialog open={modalOpen} onOpenChange={(v: boolean) => { if (!v) { setModalOpen(false); setSelectedCobro(null); setAbonoForm({ numeroDocumento: '', transferencia: '', boleta: '', fecha: '' }); setError(null); } }}>
          <DialogContent className="max-w-2xl bg-background rounded-lg shadow-xl border border-border max-h-[90vh] overflow-y-auto">
            <DialogHeader className="pb-4 border-b border-border">
              <DialogTitle className="text-2xl font-semibold text-foreground">Registrar Abono</DialogTitle>
              <p className="text-sm text-muted-foreground mt-1">Complete la información del pago recibido</p>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Información del cobro seleccionado - Estilo Notion */}
              {selectedCobro && (() => {
                const lotIds = selectedCobro.lotes.split(',').map((id: string) => id.trim());
                const lotesGrupo = lots.filter((lot: any) => lotIds.includes(lot.id));
                const lot = lotesGrupo.length > 0 ? lotesGrupo[0] : null;
                const calculo = lot ? calcularCuotasPendientes(lot) : null;

                return (
                  <div className="bg-muted/30 border border-border rounded-lg p-4">
                    <div className="flex items-center mb-3">
                      <div className="h-8 w-8 rounded-md bg-blue-500/20 flex items-center justify-center mr-3">
                        <svg className="h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground">Detalles de la Cuota</h3>
                        <p className="text-xs text-muted-foreground">Información del pago a registrar</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                      <div>
                        <Label className="text-xs font-medium text-muted-foreground">Comprador</Label>
                        <p className="text-sm font-medium text-foreground mt-0.5">{selectedCobro.comprador}</p>
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-muted-foreground">Lote(s)</Label>
                        <p className="text-sm font-medium text-foreground mt-0.5 font-mono">{selectedCobro.lotes}</p>
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-muted-foreground">Cuota</Label>
                        <p className="text-sm font-semibold text-blue-500 mt-0.5">Cuota {selectedCobro.cuota}</p>
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-muted-foreground">Fecha de Vencimiento</Label>
                        <p className="text-sm font-medium text-foreground mt-0.5">{formatGTDate(selectedCobro.fechaVencimiento)}</p>
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-muted-foreground">Monto de la Cuota</Label>
                        <p className="text-sm font-semibold text-emerald-500 mt-0.5">Q. {parseFloat(selectedCobro.montoCuota).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                      </div>
                      {calculo && (
                        <div>
                          <Label className="text-xs font-medium text-muted-foreground">Saldo Pendiente</Label>
                          <p className="text-sm font-semibold text-orange-500 mt-0.5">Q. {calculo.saldoPendiente.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Formulario - Estilo Notion */}
              <div className="space-y-5">
                {/* Sección: Datos Bancarios */}
                <div>
                  <h3 className="text-sm font-medium text-foreground mb-3 flex items-center">
                    <svg className="h-4 w-4 mr-2 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    Datos Bancarios
                  </h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="numero-doc-abono" className="text-xs font-medium text-muted-foreground">
                        No. Doc. del Banco <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="numero-doc-abono"
                        value={abonoForm.numeroDocumento}
                        onChange={e => setAbonoForm(f => ({ ...f, numeroDocumento: e.target.value }))}
                        placeholder="Ej: 9186345"
                        required
                        className="h-10 bg-secondary border-input focus:border-ring focus:ring-1 focus:ring-ring hover:border-ring transition-colors rounded-md text-foreground placeholder:text-muted-foreground"
                      />
                    </div>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="transferencia" className="text-xs font-medium text-muted-foreground">
                          No. Autorización <span className="text-muted-foreground/70">(Opcional)</span>
                        </Label>
                        <Input
                          id="transferencia"
                          value={abonoForm.transferencia}
                          onChange={e => setAbonoForm(f => ({ ...f, transferencia: e.target.value }))}
                          placeholder="Ej: 123456789"
                          className="h-10 bg-secondary border-input focus:border-ring focus:ring-1 focus:ring-ring hover:border-ring transition-colors rounded-md text-foreground placeholder:text-muted-foreground"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sección: Fecha del Pago */}
                <div>
                  <h3 className="text-sm font-medium text-foreground mb-3 flex items-center">
                    <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
                    Fecha del Pago
                  </h3>
                  <div className="space-y-2">
                    <Label htmlFor="fecha-abono" className="text-xs font-medium text-muted-foreground">
                      Fecha de Abono <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="fecha-abono"
                      type="date"
                      value={abonoForm.fecha}
                      onChange={e => setAbonoForm(f => ({ ...f, fecha: e.target.value }))}
                      className="h-10 bg-secondary border-input focus:border-ring focus:ring-1 focus:ring-ring hover:border-ring transition-colors rounded-md text-foreground [color-scheme:light] dark:[color-scheme:dark]"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Mensajes de error - Estilo Notion */}
              {error && (
                <div className="flex items-start gap-3 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-destructive">Error</p>
                    <p className="text-sm text-destructive mt-0.5">{error}</p>
                  </div>
                </div>
              )}

              {/* Botones - Estilo Notion */}
              <div className="flex justify-between items-center pt-4 border-t border-border">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setModalOpen(false);
                    setSelectedCobro(null);
                    setAbonoForm({ numeroDocumento: '', transferencia: '', boleta: '', fecha: '' });
                    setError(null);
                  }}
                  className="text-muted-foreground hover:text-foreground hover:bg-accent h-9 px-4 text-sm font-medium"
                >
                  Cancelar
                </Button>

                <Button
                  onClick={handleGuardarAbono}
                  disabled={!abonoForm.numeroDocumento || !abonoForm.fecha}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-4 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all"
                >
                  <Check className="h-4 w-4 mr-2" />
                  Registrar Abono
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Modal de Abono Manual - Estilo Notion */}
      {modalAbonoManualOpen && (
        <Dialog open={modalAbonoManualOpen} onOpenChange={setModalAbonoManualOpen}>
          <DialogContent className="max-w-5xl bg-background rounded-lg shadow-xl border border-border max-h-[95vh] overflow-y-auto">
            <DialogHeader className="pb-3 border-b border-border">
              <DialogTitle className="text-xl font-semibold text-foreground">Registrar Abono Manual</DialogTitle>
              <p className="text-xs text-muted-foreground mt-1">Complete la información del pago recibido</p>
            </DialogHeader>

            <div className="space-y-4 py-3">
              {/* Selector de lotes múltiples con combobox - Estilo Notion */}
              <div className="space-y-2">
                <Label htmlFor="lote-select" className="text-xs font-medium text-muted-foreground">
                  Seleccionar Lote(s) <span className="text-destructive">*</span>
                </Label>
                <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={comboboxOpen}
                      className="w-full justify-between h-10 bg-secondary border-input hover:border-ring hover:bg-accent text-left font-normal transition-colors"
                    >
                      <span className={selectedLotesManual.length > 0 ? "text-foreground" : "text-muted-foreground"}>
                        {selectedLotesManual.length > 0
                          ? `${selectedLotesManual.length} lote(s) seleccionado(s)`
                          : "Selecciona uno o más lotes a crédito..."}
                      </span>
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-muted-foreground" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0 bg-popover border border-border shadow-lg rounded-lg">
                    <Command className="bg-popover">
                      <CommandInput
                        placeholder="Buscar lote o comprador..."
                        className="bg-transparent border-0 focus:ring-0 focus:outline-none h-10 px-3 text-sm text-foreground placeholder:text-muted-foreground"
                      />
                      <CommandList className="bg-popover max-h-64 overflow-y-auto">
                        <CommandEmpty className="py-6 text-muted-foreground text-center text-sm">No se encontraron lotes.</CommandEmpty>
                        <CommandGroup className="bg-popover p-1">
                          {lots
                            .filter((lot: any) => lot.status === 'crédito' && lot.paymentType === 'pagos' && lot.amountPaid < lot.totalValue)
                            .map((lot: any) => {
                              const calculo = calcularCuotasPendientes(lot);
                              const yaSeleccionado = selectedLotesManual.some(item => item.lot.id === lot.id);
                              return (
                                <CommandItem
                                  key={lot.id}
                                  value={`${lot.id} ${lot.buyerName}`}
                                  onSelect={() => {
                                    if (yaSeleccionado) {
                                      // Remover si ya está seleccionado
                                      setSelectedLotesManual(prev => prev.filter(item => item.lot.id !== lot.id));
                                    } else {
                                      // Agregar nuevo lote con monto inicial vacío
                                      setSelectedLotesManual(prev => [...prev, { lot, monto: '' }]);
                                    }
                                    setComboboxOpen(false);
                                  }}
                                  className={`py-2 px-3 cursor-pointer hover:bg-accent rounded-md transition-colors ${yaSeleccionado ? 'bg-blue-500/10' : ''}`}
                                >
                                  <div className="flex items-center justify-between w-full">
                                    <div className="flex flex-col space-y-0.5 flex-1">
                                      <div className="font-medium text-sm text-foreground">
                                        {lot.id} - {lot.buyerName}
                                      </div>
                                      <div className="text-xs text-muted-foreground">
                                        Pendiente: Q. {calculo.saldoPendiente.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                      </div>
                                    </div>
                                    {yaSeleccionado && (
                                      <Check className="h-4 w-4 text-blue-500 ml-2" />
                                    )}
                                  </div>
                                </CommandItem>
                              );
                            })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Tabla de lotes seleccionados */}
              {selectedLotesManual.length > 0 && (
                <div className="bg-card border border-border rounded-lg overflow-hidden">
                  <div className="p-2 bg-muted/30 border-b border-border">
                    <h3 className="text-xs font-semibold text-foreground">Lotes Seleccionados</h3>
                    <p className="text-xs text-muted-foreground">Ingresa el monto del abono para cada lote</p>
                  </div>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/30 border-b border-border">
                          <TableHead className="w-24 py-2 text-xs text-muted-foreground">Lote</TableHead>
                          <TableHead className="py-2 text-xs text-muted-foreground">Comprador</TableHead>
                          <TableHead className="text-right py-2 text-xs text-muted-foreground">Saldo Pendiente</TableHead>
                          <TableHead className="text-right w-48 py-2 text-xs text-muted-foreground">Monto Abono</TableHead>
                          <TableHead className="w-12 py-2"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedLotesManual.map((item, index) => {
                          const calculo = calcularCuotasPendientes(item.lot);
                          // Detectar si es grupo (tiene idLotes)
                          const esGrupo = Array.isArray(item.lot.idLotes) && item.lot.idLotes.length > 1;
                          return (
                            <TableRow key={item.lot.id || index} className="border-b border-border hover:bg-muted/10">
                              <TableCell className="font-mono text-xs font-medium py-2 text-foreground">
                                {esGrupo ? item.lot.idLotes.join(', ') : item.lot.id}
                              </TableCell>
                              <TableCell className="text-xs py-2 text-foreground">{item.lot.buyerName}</TableCell>
                              <TableCell className="text-right text-xs font-medium text-orange-500 py-2">
                                Q. {calculo.saldoPendiente.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                              </TableCell>
                              <TableCell className="py-2">
                                <div className="relative w-full">
                                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs z-10">Q</span>
                                  <Input
                                    type="number"
                                    value={item.monto}
                                    onChange={(e) => {
                                      const nuevoMonto = e.target.value;
                                      setSelectedLotesManual(prev =>
                                        prev.map((lotItem, idx) =>
                                          idx === index ? { ...lotItem, monto: nuevoMonto } : lotItem
                                        )
                                      );
                                    }}
                                    placeholder="0.00"
                                    step="0.01"
                                    min="0"
                                    max={calculo.saldoPendiente}
                                    className="pl-6 pr-2 h-8 text-right text-xs bg-secondary border-input focus:border-ring focus:ring-1 focus:ring-ring w-full min-w-[120px] text-foreground placeholder:text-muted-foreground"
                                  />
                                </div>
                              </TableCell>
                              <TableCell className="py-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedLotesManual(prev => prev.filter((_, idx) => idx !== index));
                                  }}
                                  className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                  {/* Resumen total */}
                  <div className="p-2 bg-muted/30 border-t border-border">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-semibold text-foreground">Total:</span>
                      <span className="text-base font-bold text-blue-500">
                        Q. {selectedLotesManual.reduce((sum, item) => {
                          const monto = parseFloat(item.monto) || 0;
                          return sum + monto;
                        }, 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Mostrar información detallada solo si hay un solo lote seleccionado (modo legacy) */}
              {selectedLotManual && selectedLotesManual.length === 0 && (() => {
                const calculo = calcularCuotasPendientes(selectedLotManual);
                return (
                  <div className="bg-muted/30 border border-border rounded-lg p-4">
                    <div className="flex items-center mb-3">
                      <div className="h-8 w-8 rounded-md bg-blue-500/20 flex items-center justify-center mr-3">
                        <svg className="h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground">Detalles del Lote</h3>
                        <p className="text-xs text-muted-foreground">Información financiera</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                      <div>
                        <Label className="text-xs font-medium text-muted-foreground">Comprador</Label>
                        <p className="text-sm font-medium text-foreground mt-0.5">{selectedLotManual.buyerName}</p>
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-muted-foreground">Lote</Label>
                        <p className="text-sm font-medium text-foreground mt-0.5 font-mono">{selectedLotManual.id}</p>
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-muted-foreground">Valor Total</Label>
                        <p className="text-sm font-semibold text-foreground mt-0.5">Q. {selectedLotManual.totalValue?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-muted-foreground">Enganche</Label>
                        <p className="text-sm font-semibold text-blue-500 mt-0.5">Q. {calculo.enganche.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-muted-foreground">Pagado en Cuotas</Label>
                        <p className="text-sm font-semibold text-green-600 mt-0.5">Q. {calculo.abonosPagados.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-muted-foreground">Saldo Pendiente</Label>
                        <p className="text-sm font-semibold text-orange-500 mt-0.5">Q. {calculo.saldoPendiente.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Formulario - Estilo Notion */}
              <div className="space-y-3">
                {/* Sección: Información del Pago */}
                <div>
                  <h3 className="text-xs font-medium text-foreground mb-2 flex items-center">
                    <DollarSign className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
                    Información del Pago
                  </h3>
                  <div className={`grid ${selectedLotesManual.length > 0 ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
                    {/* Mostrar campo de monto solo si hay un solo lote (modo legacy) */}
                    {selectedLotesManual.length === 0 && (
                      <div className="space-y-2">
                        <Label htmlFor="monto-manual" className="text-xs font-medium text-muted-foreground">
                          Monto del Abono <span className="text-destructive">*</span>
                        </Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">Q</span>
                          <Input
                            id="monto-manual"
                            type="number"
                            value={abonoManualForm.monto}
                            onChange={(e) => setAbonoManualForm({ ...abonoManualForm, monto: e.target.value })}
                            placeholder="0.00"
                            step="0.01"
                            min="0"
                            className="pl-8 h-10 bg-secondary border-input focus:border-ring focus:ring-1 focus:ring-ring hover:border-ring transition-colors rounded-md text-foreground placeholder:text-muted-foreground"
                          />
                        </div>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="fecha-manual" className="text-xs font-medium text-muted-foreground">
                        Fecha del Abono <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="fecha-manual"
                        type="date"
                        value={abonoManualForm.fecha}
                        onChange={(e) => setAbonoManualForm({ ...abonoManualForm, fecha: e.target.value })}
                        className="h-9 bg-secondary border-input focus:border-ring focus:ring-1 focus:ring-ring hover:border-ring transition-colors rounded-md text-foreground [color-scheme:light] dark:[color-scheme:dark]"
                      />
                    </div>
                  </div>
                </div>

                {/* Sección: Datos Bancarios */}
                <div>
                  <h3 className="text-sm font-medium text-foreground mb-3 flex items-center">
                    <svg className="h-4 w-4 mr-2 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    Datos Bancarios
                  </h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="numero-doc-manual" className="text-xs font-medium text-muted-foreground">
                        No. Doc. del Banco <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="numero-doc-manual"
                        value={abonoManualForm.numeroDocumento}
                        onChange={(e) => setAbonoManualForm({ ...abonoManualForm, numeroDocumento: e.target.value })}
                        placeholder="Ej: 9186345"
                        required
                        className="h-9 bg-secondary border-input focus:border-ring focus:ring-1 focus:ring-ring hover:border-ring transition-colors rounded-md text-foreground placeholder:text-muted-foreground"
                      />
                    </div>
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label htmlFor="transferencia-manual" className="text-xs font-medium text-muted-foreground">
                          No. Autorización <span className="text-muted-foreground/70">(Opcional)</span>
                        </Label>
                        <Input
                          id="transferencia-manual"
                          value={abonoManualForm.transferencia}
                          onChange={(e) => setAbonoManualForm({ ...abonoManualForm, transferencia: e.target.value })}
                          placeholder="Ej: 123456789"
                          className="h-9 bg-secondary border-input focus:border-ring focus:ring-1 focus:ring-ring hover:border-ring transition-colors rounded-md text-foreground placeholder:text-muted-foreground"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sección: Observaciones */}
                <div className="space-y-2">
                  <Label htmlFor="observaciones-manual" className="text-xs font-medium text-muted-foreground">
                    Observaciones <span className="text-muted-foreground/70">(Opcional)</span>
                  </Label>
                  <Input
                    id="observaciones-manual"
                    value={abonoManualForm.observaciones}
                    onChange={(e) => setAbonoManualForm({ ...abonoManualForm, observaciones: e.target.value })}
                    placeholder="Notas adicionales sobre el abono..."
                    className="h-9 bg-secondary border-input focus:border-ring focus:ring-1 focus:ring-ring hover:border-ring transition-colors rounded-md text-foreground placeholder:text-muted-foreground"
                  />
                </div>
              </div>

              {/* Preview del cálculo - Estilo Notion */}
              {previewCalculoManual && (
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                  <div className="flex items-center mb-4">
                    <div className="h-8 w-8 rounded-md bg-blue-600 flex items-center justify-center mr-3">
                      <Calculator className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-blue-500">Resumen de Aplicación</h3>
                      <p className="text-xs text-blue-400">Distribución del pago</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs font-medium text-blue-500">Aplicado a Cuota Actual</Label>
                        <p className="text-sm font-semibold text-foreground mt-1">Q. {previewCalculoManual.cuotaAplicada.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-blue-500">Excedente</Label>
                        <p className="text-sm font-semibold text-foreground mt-1">Q. {previewCalculoManual.excedente.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                      </div>
                    </div>

                    <div className="bg-background rounded-md p-3 border border-border">
                      <Label className="text-xs font-medium text-blue-500">Cuotas Futuras Aplicadas</Label>
                      <p className="text-sm font-medium text-foreground mt-1">
                        {previewCalculoManual.cuotasFuturasAfectadas.length > 0
                          ? `Cuotas ${previewCalculoManual.cuotasFuturasAfectadas.join(', ')} (${previewCalculoManual.cuotasFuturasAfectadas.length} cuotas)`
                          : 'Ninguna'
                        }
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs font-medium text-blue-500">Saldo Después</Label>
                        <p className="text-sm font-semibold text-foreground mt-1">Q. {previewCalculoManual.saldoDespues.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-blue-500">Cuotas Restantes</Label>
                        <p className="text-sm font-semibold text-foreground mt-1">{previewCalculoManual.cuotasRestantes}</p>
                      </div>
                    </div>

                    {previewCalculoManual.excedente > 0 && (
                      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 flex gap-2">
                        <svg className="h-5 w-5 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                          <p className="text-xs font-medium text-amber-500">Nota</p>
                          <p className="text-xs text-amber-400 mt-0.5">
                            El excedente de Q. {previewCalculoManual.excedente.toLocaleString(undefined, { minimumFractionDigits: 2 })} se aplicará como pago adelantado a la siguiente cuota disponible.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Mensajes de error - Estilo Notion */}
              {error && (
                <div className="flex items-start gap-3 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-destructive">Error</p>
                    <p className="text-sm text-destructive mt-0.5">{error}</p>
                  </div>
                </div>
              )}

              {/* Botones - Estilo Notion */}
              <div className="flex justify-between items-center pt-3 border-t border-border sticky bottom-0 bg-background pb-2 mt-2">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setModalAbonoManualOpen(false);
                    setSelectedLotManual(null);
                    setSelectedLotesManual([]);
                    setAbonoManualForm({
                      monto: '',
                      fecha: new Date().toISOString().split('T')[0],
                      numeroDocumento: '',
                      transferencia: '',
                      boleta: '',
                      observaciones: ''
                    });
                    setPreviewCalculoManual(null);
                    setError(null);
                  }}
                  className="text-muted-foreground hover:text-foreground hover:bg-accent h-8 px-3 text-xs font-medium"
                >
                  Cancelar
                </Button>

                <Button
                  onClick={handleRegistrarAbonoManual}
                  disabled={
                    (!selectedLotManual && selectedLotesManual.length === 0) ||
                    (!abonoManualForm.numeroDocumento || !abonoManualForm.fecha) ||
                    (selectedLotesManual.length === 0 && !abonoManualForm.monto) ||
                    (selectedLotesManual.length > 0 && selectedLotesManual.some(item => !item.monto || parseFloat(item.monto) <= 0))
                  }
                  className="bg-primary text-primary-foreground hover:bg-primary/90 h-8 px-3 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all"
                >
                  <Check className="h-3.5 w-3.5 mr-1.5" />
                  Registrar Abono{selectedLotesManual.length > 1 ? 's' : ''}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}